import { VerticalSpace } from '@brokerbay/design-components';
import { getPrimaryMLSNumber } from '@bb/users';
import axios from 'axios';
import {
  Button,
  Checkbox,
  Input,
  Page,
  Select,
  Space,
  Text,
  message,
} from 'components';
import { AuthContext } from 'contexts';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import capitalize from 'utils/capitalize';
import BookingInputs, { BookingInputsProps } from './BookingInputs';
import Instructions from './Instructions';
import ListingInputs, { ListingInputsProps } from './ListingInputs';

enum FlowType {
  Login = 'login',
  Booking = 'booking',
  Listing = 'configure listing / listing shell',
}

const getAuthHost = () => {
  const { host } = window.location;
  const subdomain = host.split('.')[0];
  const processedHost = subdomain === 'edge' ? 'auth' : `auth-${subdomain}`;
  return `https://${processedHost}.brokerbay.com`;
};

const TestSsoFlowPage = () => {
  const { auth } = useContext(AuthContext);
  const [userId, setUserId] = useState(auth.id);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldClaim, setShouldClaim] = useState(false);
  const [flowType, setFlowType] = useState<FlowType>(FlowType.Login);
  const [listings, setListings] = useState<BookingInputsProps['listings']>([
    { id: undefined, mlsNumber: '', oid: '' },
  ]);
  const [mlsType, setMlsType] = useState('');
  const [listingMlsNumber, setListingMlsNumber] = useState('');
  const [listingParameters, setListingParameters] = useState<
    ListingInputsProps['listingParameters']
  >([]);

  const getSsoUrl = () => {
    switch (flowType) {
      case FlowType.Login:
        return `${getAuthHost()}/login/oidc/brokerbay`;

      case FlowType.Booking: {
        const urlParams = new URLSearchParams({
          mls: mlsType.trim(),
          // @ts-expect-error: string vs ObjectId
          userId: getPrimaryMLSNumber(auth.mls_numbers)?.number || '',
        });
        listings.forEach(({ mlsNumber, oid }) =>
          urlParams.append('mlsNumber', `${oid.trim()},${mlsNumber.trim()}`),
        );
        return `${getAuthHost()}/showing/oidc/brokerbay?${urlParams.toString()}`;
      }

      case FlowType.Listing: {
        const urlParams = new URLSearchParams({
          mls: mlsType.trim(),
          mlsNumber: listingMlsNumber.trim(),
        });
        listingParameters.forEach(({ key, value }) =>
          urlParams.append(key.trim(), value.trim()),
        );
        return `${getAuthHost()}/listing/oidc/brokerbay?${urlParams.toString()}`;
      }

      default:
        return `${getAuthHost()}/login/oidc/brokerbay`;
    }
  };

  const ssoUrl = getSsoUrl();

  const setUserClaimStatus = async () => {
    try {
      await axios.post('/onboarding/qa_page/reset_user', {
        userId,
        shouldClaim,
      });

      message.success(
        `User ${userId} is now ${shouldClaim ? '' : 'un'}claimed`,
      );
    } catch (error) {
      message.error(error?.toString(), 8);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Page type="filled" title="Test OIDC SSO Flow QA Page" fillScreenHeight>
      <Layout>
        <VerticalSpace>
          <Text>This QA page is used to help test OIDC SSO flows</Text>
          <Instructions />
          <StyledInput
            addonAfter={
              <Button
                disabled={isLoading}
                loading={isLoading}
                onClick={() => setUserClaimStatus()}
                data-cy="testOidc-claim-button"
              >
                {shouldClaim ? 'Claim' : 'Unclaim'} User
              </Button>
            }
            onChange={e => setUserId(e.target.value)}
            label="User Id"
            placeholder="User Id"
            defaultValue={auth.id}
            data-cy="testOidc-input-claimUserId"
          />
          <Checkbox
            checked={shouldClaim}
            onChange={e => setShouldClaim(e.target.checked)}
            data-cy="testOidc-checkbox-shouldClaim"
          >
            Claim Status
          </Checkbox>
          <StyledSelect
            value={flowType}
            onChange={value => setFlowType(value)}
            options={Object.values(FlowType).map(opt => ({
              label: capitalize(opt),
              value: opt,
            }))}
            label="Flow Type"
            data-cy="testOidc-select-flowType"
          />

          {flowType === FlowType.Booking && (
            <BookingInputs
              listings={listings}
              setListings={setListings}
              mlsType={mlsType}
              setMlsType={setMlsType}
            />
          )}

          {flowType === FlowType.Listing && (
            <ListingInputs
              mlsType={mlsType}
              setMlsType={setMlsType}
              listingMlsNumber={listingMlsNumber}
              setListingMlsNumber={setListingMlsNumber}
              listingParameters={listingParameters}
              setListingParameters={setListingParameters}
            />
          )}

          <Space>
            <Button href={ssoUrl} data-cy="testOidc-sso-button">
              SSO
            </Button>
            <Button
              onClick={() => navigator.clipboard.writeText(ssoUrl)}
              data-cy="testOidc-copyLink-button"
            >
              Copy SSO Link
            </Button>
          </Space>
        </VerticalSpace>
      </Layout>
    </Page>
  );
};

export default TestSsoFlowPage;

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;
  padding: 20px;
  background: white;
`;

const StyledInput = styled(Input)`
  width: 500px;
`;

const StyledSelect = styled(Select)`
  width: 500px;
`;
